import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { getUserDisplayName as formatUserDisplayName } from '@realadvisor/transform/user-display-name';

import { gql } from '@graphql/__generated__';
import type {
  UserCardDisplayNameFragment,
  UserCard_UserFragment,
} from '@graphql/__generated__/graphql';
import {
  COMPACT_USER_AVATAR_MIN_WIDTH,
  COMPACT_USER_AVATAR_SIZE,
  UserAvatar,
} from '@modules/_shared/components/UserAvatar';
import { formatPhone } from '@modules/_shared/utils/formatting';

export const USER_CARD_DISPLAY_NAME_FRAGMENT = gql(/* GraphQL */ `
  fragment UserCardDisplayName on users {
    id
    first_name
    last_name
    full_name
    emails(
      order_by: [{ primary: desc }, { verified: desc }, { created_at: desc }]
      limit: 1
    ) {
      id
      email
      primary
    }
  }
`);

export const USER_CARD_FRAGMENT = gql(/* GraphQL */ `
  fragment UserCard_user on users {
    id
    ...UserAvatar
    ...UserCardDisplayName
    phone_numbers(order_by: [{ primary: desc }, { created_at: asc }]) {
      id
      primary
      number
      type
      verified
    }
  }
`);

interface UserCardProps {
  user: UserCard_UserFragment;
  condensed?: boolean;
  userActions?: React.ReactNode;
  extraItems?: React.ReactNode;
  // Rendered inline right after the name (e.g. a small status/badge chip).
  nameAdornment?: React.ReactNode;
  secondaryText?: React.ReactNode;
  onSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selected?: boolean;
  forceCheckbox?: boolean;
  alignItems?: 'center' | 'flex-start';
  avatarSize?: number;
  compact?: boolean;
}

export const UserCardSkeleton: React.FC<{
  hasSecondaryText?: boolean;
  condensed?: boolean;
}> = ({ hasSecondaryText = false, condensed = false }) => {
  const size = condensed ? 40 : 70;

  return (
    <List
      dense={condensed}
      sx={[
        { display: 'flex', flexDirection: { xs: 'column', sm: 'row' } },
        condensed ? { p: 0 } : { columnGap: 3 },
      ]}
    >
      <ListItem sx={condensed ? { py: 0 } : {}} disableGutters>
        <ListItemAvatar sx={condensed ? {} : { mr: 1.5 }}>
          <Skeleton variant="circular">
            <Avatar sx={{ width: size, height: size }} />
          </Skeleton>
        </ListItemAvatar>
        <ListItemText
          sx={theme => ({
            '& .MuiTypography-root': theme.mixins.truncate(),
            ...(condensed ? { my: 0 } : {}),
          })}
          primary={
            <Skeleton width="80%">
              <Typography variant={condensed ? 'body1' : 'h6'}>
                Loading
              </Typography>
            </Skeleton>
          }
          secondary={
            hasSecondaryText && (
              <Skeleton width="50%">
                <Typography variant="body2">loading</Typography>
              </Skeleton>
            )
          }
        />
      </ListItem>
    </List>
  );
};

export const getUserDisplayName = (
  user: UserCardDisplayNameFragment,
  skipId = false,
) =>
  formatUserDisplayName(user, {
    fallbackToId: !skipId,
    trimCombinedName: false,
  });

export const UserCard: React.FC<UserCardProps> = ({
  user,
  condensed = false,
  userActions,
  extraItems,
  nameAdornment,
  secondaryText,
  onSelect,
  forceCheckbox = false,
  selected,
  alignItems,
  avatarSize,
  compact = false,
}) => {
  const primaryPhone =
    user.phone_numbers.find(p => p.primary) ?? user.phone_numbers[0];
  const subTitle = secondaryText ?? formatPhone(primaryPhone?.number ?? null);
  const nameToDisplay = getUserDisplayName(user);
  return (
    <List
      dense={condensed}
      sx={[
        { display: 'flex', flexDirection: { xs: 'column', sm: 'row' } },
        condensed ? { p: 0 } : { columnGap: 3 },
      ]}
    >
      <ListItem
        sx={[
          {
            '& .MuiListItemSecondaryAction-root': {
              transform: 'none',
              position: 'static',
              whiteSpace: 'nowrap',
            },
          },
          condensed ? { py: 0 } : {},
          userActions != null ? { pr: 0 } : {},
        ]}
        disableGutters
        secondaryAction={userActions}
        alignItems={alignItems}
      >
        <ListItemAvatar
          sx={[
            condensed ? { mt: 0 } : { mr: 1.5 },
            compact && { minWidth: COMPACT_USER_AVATAR_MIN_WIDTH },
          ]}
        >
          <UserAvatar
            user={user}
            size={
              avatarSize ??
              (compact ? COMPACT_USER_AVATAR_SIZE : condensed ? 40 : 60)
            }
            onSelect={onSelect}
            forceCheckbox={forceCheckbox}
            selected={selected}
            nameFallback={nameToDisplay}
          />
        </ListItemAvatar>
        <ListItemText
          sx={theme => ({
            ...(condensed ? { my: 0 } : {}),
            '& .MuiListItemText-secondary': theme.mixins.truncate(),
            '& .MuiListItemText-primary': theme.mixins.truncate(),
          })}
          primary={
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minWidth: 0,
              }}
            >
              <Typography
                component="span"
                variant={compact ? 'body2' : 'body1'}
                noWrap
                sx={{ fontWeight: 500, minWidth: 0 }}
              >
                {nameToDisplay}
              </Typography>
              {nameAdornment != null && (
                <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                  {nameAdornment}
                </span>
              )}
            </span>
          }
          secondary={compact ? secondaryText : subTitle}
        />
      </ListItem>
      {extraItems}
    </List>
  );
};
